import React, { useEffect } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { NavListItemProps } from 'components/molecules/NavListItem/NavListItem';
import weakKey from 'weak-key';

import { BackLink, Image, Typography } from '../../components/atoms';
import { LoadingSpinner, Page } from '../../components/layouts';
import { NavList } from '../../components/molecules';
import * as Api from '../../weborder/Api';
import { StateProps } from '../../weborder/component/ConcessionList';
import * as Routes from '../../weborder/Routes';
import { domUtils } from '../../weborder/utils';

type Props = StateProps & Routes.Routes;

const useStyles = makeStyles((theme: Theme) => ({
  image: {
    marginRight: theme.spacing(2),
  },
  navList: {
    '& a': {
      padding: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(2, 0),
      },
    },
  },
  storeInfo: {
    alignItems: 'center',
    display: 'flex',
    '& .MuiTypography-body1': {
      marginBottom: theme.spacing(0.25),
    },
    '& .MuiTypography-body2': {
      color: theme.palette.grey[800],
    },
    '&.MuiListItem-root': {
      backgroundColor: 'red',
    },
    '& picture, & img': {
      display: 'block',
    },
  },
}));

export const StoreConcessionList = (props: Props): JSX.Element => {
  const {
    CollectionSummaries: collectionSummaries,
    Menus: menus,
    params,
    translate,
  } = props;

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  const classes = useStyles();

  const getCollectionSummary = (): Api.CollectionRestaurantSummary => {
    if (collectionSummaries.status !== 'success') {
      return null;
    }

    const versionIndex = collectionSummaries.data.findIndex(
      (Summary) =>
        Summary.PhysicalRestaurantId === parseInt(params.prid as any),
    );

    if (versionIndex === -1) {
      return null;
    }

    const summary = collectionSummaries.data[versionIndex];
    return summary;
  };

  const renderStoreList = (summary): NavListItemProps[] => {
    if (summary === null) {
      return null;
    }

    const menuUrl = summary.MenuUrl;
    const menu = menus[menuUrl];

    if (!menuUrl || !menu || menu.status !== 'success') {
      return null;
    }

    const concessionList = menu.data.ConcessionStores.map(
      (store): NavListItemProps => {
        const linkPath = `/restaurant/${summary.VirtualRestaurantId}/collection/${summary.PhysicalRestaurantId}/${store.ConcessionStoreId}`;

        return {
          elements: [
            <div
              className={classes.storeInfo}
              key={weakKey({ id: store.ConcessionStoreId })}
            >
              <Image
                alt={store.Name}
                className={classes.image}
                dimensions={[72, 72]}
                src={store.ImageUrl}
              />
              <div>
                <Typography>
                  <em>{store.Name}</em>
                </Typography>
                <Typography variant="body2">{store.Description}</Typography>
              </div>
            </div>,
          ],
          linkCallback: () => linkPath,
          linkId: linkPath,
          linkPath: linkPath,
        };
      },
    );

    return concessionList;
  };

  const summary = getCollectionSummary();

  const storeName = summary && summary.PhysicalRestaurantName;

  if (collectionSummaries.status !== 'success') {
    return <LoadingSpinner />;
  }

  return (
    <Page pageName="store-concession-list">
      <header>
        <BackLink
          label={translate('Back')}
          linkPath="/collection"
          testId="collection-back"
        />

        <Typography variant="h1">{storeName}</Typography>
      </header>

      <article>
        <NavList
          className={classes.navList}
          items={renderStoreList(summary)}
        ></NavList>
      </article>
    </Page>
  );
};
